import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "sonner";

interface SignatureDialogProps {
  open: boolean;
  onClose: () => void;
  onSign: (signatureData: string, signatureName: string) => Promise<void>;
  estimateTotal: number;
  customerName: string;
}

export function SignatureDialog({ open, onClose, onSign, estimateTotal, customerName }: SignatureDialogProps) {
  const [signatureName, setSignatureName] = useState(customerName || "");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const signatureRef = useRef<SignatureCanvas>(null);

  const handleClear = () => {
    signatureRef.current?.clear();
  };

  const handleSubmit = async () => {
    if (!signatureName.trim()) {
      toast.error("Please enter your full name");
      return;
    }

    if (signatureRef.current?.isEmpty()) {
      toast.error("Please provide your signature");
      return;
    }

    if (!agreedToTerms) {
      toast.error("Please agree to the terms to continue");
      return;
    }

    try {
      setIsSubmitting(true);
      const signatureData = signatureRef.current?.toDataURL() || "";
      await onSign(signatureData, signatureName);
      toast.success("Proposal accepted successfully!");
      onClose();
    } catch (error) {
      toast.error("Failed to submit signature");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Accept & Sign Proposal</DialogTitle>
          <DialogDescription>
            By signing below, you agree to proceed with the roofing project as outlined in this proposal.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="bg-muted/30 rounded-lg p-4 border">
            <div className="flex justify-between items-center">
              <span className="font-medium">Total Investment:</span>
              <span className="text-2xl font-bold text-primary">
                ${estimateTotal.toLocaleString()}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="signature-name">Full Name *</Label>
            <Input
              id="signature-name"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              placeholder="Enter your full legal name"
            />
          </div>

          <div className="space-y-2">
            <Label>Sign Here *</Label>
            <div className="border-2 border-muted rounded-lg bg-background">
              <SignatureCanvas
                ref={signatureRef}
                canvasProps={{
                  className: "w-full h-48 touch-none",
                  style: { width: "100%", height: "192px" }
                }}
              />
            </div>
            <Button variant="outline" size="sm" onClick={handleClear}>
              Clear Signature
            </Button>
          </div>

          <div className="flex items-start space-x-2">
            <Checkbox
              id="terms"
              checked={agreedToTerms}
              onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
            />
            <label
              htmlFor="terms"
              className="text-sm leading-relaxed cursor-pointer"
            >
              I agree to the terms and scope of work outlined in this proposal, including the total investment amount and payment terms.
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : "Submit Signature"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
